import { motion } from 'framer-motion';
import { useRegularProjects } from '../hooks/useProjects';
import { useTickets } from '../hooks/useTickets';
import { useUserStore } from '../store/userStore';
import { DashboardHeader } from '../components/DashboardHeader';
import { useNavigate } from 'react-router-dom';
import type { Project } from '../types';
import { RiskBadge } from '../components/RiskBadge';
import { getRiskBandDisplay, formatRiskScore, DEFAULT_ALERT_THRESHOLD } from '../lib/riskBand';

const TYPE_LABELS: Record<string, string> = {
  feature: 'Feature',
  enhancement: 'Enhancement',
};

const STATUS_COLORS: Record<string, string> = {
  triage: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  planning: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  on_track: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  at_risk: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  idea: 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400',
};

function DesignTicketsForProject({ project }: { project: Project }) {
  const { data: features } = useTickets(project.projectId, { type: 'feature' });
  const { data: enhancements } = useTickets(project.projectId, { type: 'enhancement' });

  const tickets = [
    ...(features ?? []),
    ...(enhancements ?? []),
  ].filter((t) => t.status !== 'complete' && t.status !== 'invalid');

  if (tickets.length === 0) return null;

  return (
    <>
      {tickets.map((ticket) => (
        <div
          key={ticket.ticketId}
          className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06]"
        >
          <div className="min-w-0 flex-1">
            <p className="text-title text-gray-900 dark:text-white truncate">{ticket.title}</p>
            <p className="text-meta text-gray-500 dark:text-gray-400">{project.name}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-meta font-medium text-gray-400 dark:text-gray-500">
              {TYPE_LABELS[ticket.type] ?? ticket.type}
            </span>
            <span className={`text-meta font-semibold px-1.5 py-0.5 rounded-full ${STATUS_COLORS[ticket.status] ?? STATUS_COLORS.idea}`}>
              {ticket.status?.replace('_', ' ')}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}

function CompactProjectCard({ project }: { project: Project }) {
  const navigate = useNavigate();

  const statusColors: Record<string, string> = {
    at_risk: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    on_track: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    active: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    planning: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    draft: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    complete: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400',
  };
  const colorCls = statusColors[project.status] ?? statusColors.archived;

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => navigate(`/projects/${project.projectId}`)}
      className="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl cursor-pointer hover:shadow-sm transition-shadow"
    >
      <p className="text-title text-gray-900 dark:text-white truncate flex-1">
        {project.name}
      </p>
      <span className={`flex-shrink-0 text-meta font-semibold px-2 py-0.5 rounded-full ${colorCls}`}>
        {project.status?.replace('_', ' ')}
      </span>
      <RiskBadge score={project.overallRiskScore} size="sm" />
    </motion.div>
  );
}

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-3 w-1/3 rounded" />
    </div>
  );
}

export function DesignerDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { sessionConfig } = useUserStore();
  const alertThreshold = sessionConfig?.alertThreshold ?? DEFAULT_ALERT_THRESHOLD;
  const { data: projects, isLoading } = useRegularProjects();

  const regularProjects = projects ?? [];

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="Design Dashboard" roleLabel="Design" onBackToToday={onBackToToday} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Definition Health */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            Definition Health
          </h2>

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-12 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl overflow-hidden">
              {regularProjects.length === 0 ? (
                <p className="text-body font-medium text-gray-500 text-center py-8">No projects.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-white/[0.06]">
                  {regularProjects.map((project: Project) => {
                    const score = project.overallRiskScore;
                    const isScored = score !== null && score !== undefined && score !== 0;
                    const { label, textClass } = getRiskBandDisplay(score, alertThreshold);
                    return (
                      <li
                        key={project.projectId}
                        className="flex items-center justify-between gap-4 px-4 py-3"
                      >
                        <span className="text-title text-gray-900 dark:text-white truncate flex-1">
                          {project.name}
                        </span>
                        <span className="text-meta text-gray-400 dark:text-gray-500 flex-shrink-0">
                          Definition risk:
                        </span>
                        <span
                          title={isScored ? `Score: ${formatRiskScore(score)} / 5.0` : undefined}
                          className={`text-body font-semibold flex-shrink-0 cursor-default ${isScored ? textClass : 'text-gray-400 dark:text-gray-500'}`}
                        >
                          {isScored ? label : 'N/A'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Scope Watch Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5"
          >
            <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Scope Watch
            </h2>
            <p className="text-body text-gray-500 dark:text-gray-400">
              Scope change tracking coming soon.
            </p>
          </motion.div>

          {/* Design-Relevant Tickets */}
          <div>
            <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Design-Relevant Tickets
            </h2>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-12 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {regularProjects.map((project) => (
                  <DesignTicketsForProject key={project.projectId} project={project} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* My Projects */}
        <div className="space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            My Projects
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <ProjectSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {regularProjects.map((project) => (
                <CompactProjectCard key={project.projectId} project={project} />
              ))}
              {regularProjects.length === 0 && (
                <p className="text-body font-medium text-gray-500 text-center py-8">No projects.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
